import type { IBranchCreateData } from 'src/types/branch';

import { z as zod } from 'zod';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import { Divider, CardHeader } from '@mui/material';

import { DashboardContext } from 'src/context/dashboard/dashboard-context';

import { Form, Field } from 'src/components/hook-form';

type NewBranchSchemaType = zod.infer<typeof NewBranchSchema>;

const NewBranchSchema = zod.object({
  name: zod.string().min(3, { message: 'Şube adı en az 3 karakter olmalıdır' }),
  status: zod.enum(['ACTIVE', 'PASSIVE']),
});

interface IBranchNewEditFormProps {
  initialValues?: IBranchCreateData;
  onSubmit: (data: IBranchCreateData) => Promise<void>;
}

export function BranchNewEditForm(props: IBranchNewEditFormProps) {
  const { initialValues, onSubmit } = props;

  const { selectedRestaurant } = useContext(DashboardContext);

  const defaultValues = initialValues || {
    name: '',
    status: 'ACTIVE' as const,
  };

  const methods = useForm<NewBranchSchemaType>({
    resolver: zodResolver(NewBranchSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmitForm = handleSubmit(async (data) => {
    try {
      await onSubmit({ ...data, restaurantId: selectedRestaurant?._id || '' });
    } catch (error) {
      console.error(error);
    }
  });

  return (
    <Form methods={methods} onSubmit={onSubmitForm}>
      <Stack spacing={{ xs: 3, md: 5 }} sx={{ mx: 'auto', maxWidth: { xs: 720, xl: 880 } }}>
        <Card id="branch-form-card">
          <CardHeader
            title={` Yeni şube oluşturun`}
            subheader={`${selectedRestaurant?.name} için yeni bir şube oluşturun`}
            sx={{ mb: 3 }}
          />

          <Divider />

          <Stack spacing={3} sx={{ p: 3 }}>
            <Field.Text name="name" label="Şube Adı (Örn. Kadıköy Şube)" />

            <Field.Switch
              name="status"
              label="Aktif"
              helperText="Şube durumunu aktif veya pasif olarak ayarlayın"
              slotProps={{
                switch: {
                  color: 'primary',
                  onChange: (e) => {
                    const newValue = e.target.checked ? 'ACTIVE' : 'PASSIVE';
                    methods.setValue('status', newValue);
                  },
                  checked: methods.watch('status') === 'ACTIVE',
                },
              }}
            />

            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              sx={{ mt: 2 }}
              id="branch-submit-button"
            >
              {initialValues ? 'Şube Güncelle' : 'Şube Oluştur'}
            </LoadingButton>
          </Stack>
        </Card>
      </Stack>
    </Form>
  );
}
